import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/lib/supabase';
import { Badge } from '@/components/ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Calendar, Clock, GitBranch } from 'lucide-react';
import { format, isToday, isTomorrow, addDays } from 'date-fns';
import { cn } from '@/lib/utils';

// Mock data for simulation
const MOCK_DATA = [
  { id: '1', name: 'Complete site induction', due_date: new Date().toISOString().split('T')[0], project_id: '1', projects: { name: 'Site Alpha' }, assignees: [{ id: '1', first_name: 'John', last_name: 'Smith', avatar_url: null }], isWorkflowTask: false },
  { id: '2', name: 'Submit risk assessment', due_date: new Date().toISOString().split('T')[0], project_id: '1', projects: { name: 'Site Alpha' }, assignees: [{ id: '2', first_name: 'Sarah', last_name: 'Jones', avatar_url: null }, { id: '3', first_name: 'Mike', last_name: 'Brown', avatar_url: null }], isWorkflowTask: false },
  { id: '3', name: 'Review method statements', due_date: new Date(Date.now() + 86400000).toISOString().split('T')[0], project_id: '2', projects: { name: 'Main Office' }, assignees: [{ id: '1', first_name: 'John', last_name: 'Smith', avatar_url: null }], isWorkflowTask: true },
  { id: '4', name: 'Update permit to work', due_date: new Date(Date.now() + 172800000).toISOString().split('T')[0], project_id: '1', projects: { name: 'Site Alpha' }, assignees: [], isWorkflowTask: false },
  { id: '5', name: 'Schedule toolbox talk', due_date: new Date(Date.now() + 259200000).toISOString().split('T')[0], project_id: '3', projects: { name: 'Warehouse B' }, assignees: [{ id: '4', first_name: 'Emma', last_name: 'Wilson', avatar_url: null }], isWorkflowTask: false },
];

export function TasksDueSoonTile({ filter, useMockData }) {
  const navigate = useNavigate();
  const { company, userProfile } = useAuth();
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dimensions, setDimensions] = useState({ width: 0, height: 0 });
  const containerRef = useRef(null);

  // Observe container size changes
  useEffect(() => {
    if (!containerRef.current) return;

    const observer = new ResizeObserver((entries) => {
      const entry = entries[0];
      if (entry) {
        setDimensions({
          width: entry.contentRect.width,
          height: entry.contentRect.height,
        });
      }
    });

    observer.observe(containerRef.current);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (useMockData) {
      setData(MOCK_DATA);
      setLoading(false);
      return;
    }

    if (!company?.id || !userProfile?.id) {
      setLoading(false);
      return;
    }

    const loadData = async () => {
      setLoading(true);
      try {
        const today = new Date().toISOString().split('T')[0];
        const nextWeek = addDays(new Date(), 7).toISOString().split('T')[0];

        // Fetch regular tasks due this week
        let taskQuery = supabase
          .from('tasks')
          .select(`
            id,
            name,
            subtasks,
            due_date,
            assigned_to,
            project_id,
            projects!inner(name, company_id)
          `)
          .eq('projects.company_id', company.id)
          .gte('due_date', today)
          .lte('due_date', nextWeek)
          .order('due_date', { ascending: true })
          .limit(20);

        if (filter?.spaceId) {
          taskQuery = taskQuery.eq('project_id', filter.spaceId);
        }

        const { data: tasks, error } = await taskQuery;
        if (error) throw error;

        // Filter to tasks assigned to current user and not completed
        const activeTasks = (tasks || []).filter(task => {
          // Check if assigned to current user
          const isAssigned = Array.isArray(task.assigned_to) && task.assigned_to.includes(userProfile.id);
          if (!isAssigned) return false;

          // Check if not completed
          if (!task.subtasks || task.subtasks.length === 0) return true;
          const completedCount = task.subtasks.filter(st => st.completed).length;
          return completedCount < task.subtasks.length;
        });

        // Get assignee details
        const assigneeIds = new Set();
        activeTasks.forEach(t => {
          if (Array.isArray(t.assigned_to)) {
            t.assigned_to.forEach(id => assigneeIds.add(id));
          }
        });

        let assigneeMap = {};
        if (assigneeIds.size > 0) {
          const { data: profiles } = await supabase
            .from('user_profiles')
            .select('id, first_name, last_name, avatar_url')
            .in('id', Array.from(assigneeIds));

          (profiles || []).forEach(p => {
            assigneeMap[p.id] = p;
          });
        }

        const regularTasks = activeTasks.map(t => ({
          ...t,
          assignees: (t.assigned_to || []).map(id => assigneeMap[id]).filter(Boolean),
          isWorkflowTask: false,
        }));

        // Fetch workflow task runs due this week
        let workflowQuery = supabase
          .from('checklists_workflow_runs')
          .select(`
            id,
            run_name,
            project_id,
            status,
            started_by,
            workflows(id, name, tasks),
            projects(name)
          `)
          .eq('status', 'in_progress');

        if (filter?.spaceId) {
          workflowQuery = workflowQuery.eq('project_id', filter.spaceId);
        }

        const { data: workflowRuns, error: runsError } = await workflowQuery;
        if (runsError) throw runsError;

        let workflowTasks = [];

        if (workflowRuns && workflowRuns.length > 0) {
          const runIds = workflowRuns.map(r => r.id);

          const { data: taskRuns, error: taskRunsError } = await supabase
            .from('checklists_workflow_task_runs')
            .select('*')
            .in('workflow_run_id', runIds)
            .neq('status', 'completed')
            .gte('due_date', today)
            .lte('due_date', nextWeek);

          if (taskRunsError) throw taskRunsError;

          // Filter to tasks assigned to current user
          const myTaskRuns = (taskRuns || []).filter(taskRun => {
            const assignees = taskRun.assignees || [];
            // Handle both object format {type, id/user_id} and simple string format
            return assignees.some(a => {
              if (typeof a === 'string') return a === userProfile.id;
              if (a.type === 'user') return a.id === userProfile.id || a.user_id === userProfile.id;
              if (a.type === 'workflow_runner') {
                // Check if current user started this workflow run
                const workflowRun = workflowRuns.find(r => r.id === taskRun.workflow_run_id);
                return workflowRun?.started_by === userProfile.id;
              }
              return false;
            });
          });

          // Map to unified format
          workflowTasks = myTaskRuns.map(taskRun => {
            const workflowRun = workflowRuns.find(r => r.id === taskRun.workflow_run_id);
            const workflow = workflowRun?.workflows;
            const workflowTaskDef = (workflow?.tasks || []).find(t => String(t.id) === taskRun.task_id);

            return {
              id: taskRun.id,
              name: workflowTaskDef?.formTitle || workflowTaskDef?.name || `Task ${taskRun.task_order + 1}`,
              due_date: taskRun.due_date,
              project_id: workflowRun?.project_id,
              projects: workflowRun?.projects,
              workflow_run_id: taskRun.workflow_run_id,
              workflow_run_name: workflowRun?.run_name,
              workflow_name: workflow?.name,
              isWorkflowTask: true,
              assignees: [],
            };
          });
        }

        // Combine and sort by due date
        const allTasks = [...regularTasks, ...workflowTasks]
          .sort((a, b) => {
            if (!a.due_date && !b.due_date) return 0;
            if (!a.due_date) return 1;
            if (!b.due_date) return -1;
            return new Date(a.due_date) - new Date(b.due_date);
          })
          .slice(0, 10);

        setData(allTasks);
      } catch (err) {
        console.error('Failed to load due soon tasks:', err);
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [company?.id, userProfile?.id, filter?.spaceId, useMockData]);

  // Determine layout based on dimensions
  const { width, height } = dimensions;
  const isXS = width < 150 || height < 100;
  const isSmall = width < 200 || height < 150;
  const isWide = width >= 450; // Wide enough for table columns
  const isLarge = width >= 300 && height >= 280;

  // Calculate how many items to show
  const getVisibleCount = () => {
    if (isXS) return 0;
    if (isSmall) return 2;
    const itemHeight = 54;
    const availableHeight = height - 20;
    return Math.max(1, Math.floor(availableHeight / itemHeight));
  };

  const formatDueDate = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    if (isToday(date)) return 'Today';
    if (isTomorrow(date)) return 'Tomorrow';
    return format(date, 'EEE, MMM d');
  };

  const handleTaskClick = (task) => {
    if (task.isWorkflowTask && task.workflow_run_id) {
      navigate(`/space/${task.project_id}/workflow-run/${task.workflow_run_id}`);
    } else {
      navigate(`/space/${task.project_id}/tasks`);
    }
  };

  if (loading) {
    return (
      <div ref={containerRef} className="flex items-center justify-center h-full">
        <div className="animate-pulse space-y-3 w-full p-2">
          {[...Array(3)].map((_, i) => (
            <div key={i} className="flex items-center gap-3">
              <div className="w-8 h-8 rounded-full bg-gray-200" />
              <div className="flex-1">
                <div className="h-3 bg-gray-200 rounded w-3/4 mb-1" />
                <div className="h-2 bg-gray-200 rounded w-1/2" />
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (data.length === 0) {
    return (
      <div ref={containerRef} className="flex flex-col items-center justify-center h-full text-gray-400">
        <Calendar className="h-8 w-8 mb-2" />
        <span className="text-sm">No tasks due this week</span>
      </div>
    );
  }

  const visibleItems = data.slice(0, getVisibleCount());
  const remainingCount = data.length - visibleItems.length;

  // Group by date
  const grouped = visibleItems.reduce((acc, task) => {
    const key = task.due_date || 'no-date';
    if (!acc[key]) acc[key] = [];
    acc[key].push(task);
    return acc;
  }, {});

  // XS Layout: Just count
  if (isXS) {
    const todayCount = data.filter(t => t.due_date && isToday(new Date(t.due_date))).length;
    return (
      <div ref={containerRef} className="flex items-center justify-center h-full overflow-visible">
        <div className="text-center">
          <div className={cn('text-3xl font-bold leading-none', todayCount > 0 ? 'text-amber-600' : 'text-gray-700')}>
            {data.length}
          </div>
          <p className="text-xs text-gray-500 mt-1">due soon</p>
        </div>
      </div>
    );
  }

  // Small Layout: Count + first task
  if (isSmall) {
    const firstTask = data[0];
    return (
      <div ref={containerRef} className="flex flex-col h-full p-2 overflow-visible">
        <div className="flex items-center gap-2 mb-2">
          <div className="text-2xl font-bold text-gray-700">{data.length}</div>
          <span className="text-xs text-gray-500">due this week</span>
        </div>
        {firstTask && (
          <div
            onClick={() => handleTaskClick(firstTask)}
            className="flex items-center gap-2 p-1.5 rounded bg-gray-50 cursor-pointer hover:bg-gray-100"
          >
            {firstTask.isWorkflowTask ? (
              <GitBranch className="h-4 w-4 text-blue-500 shrink-0" />
            ) : (
              <Clock className="h-4 w-4 text-gray-400 shrink-0" />
            )}
            <div className="flex-1 min-w-0">
              <p className="text-xs font-medium truncate">{firstTask.name}</p>
              <p className="text-[10px] text-gray-500">{formatDueDate(firstTask.due_date)}</p>
            </div>
          </div>
        )}
      </div>
    );
  }

  // Wide/Large Layout: Table view with columns
  if (isWide || isLarge) {
    return (
      <div ref={containerRef} className="flex flex-col h-full p-2">
        {/* Table Header */}
        <div className={cn(
          "grid gap-2 pb-2 border-b text-xs font-medium text-gray-500",
          isWide ? "grid-cols-[1fr_80px_120px_100px]" : "grid-cols-[1fr_80px_100px]"
        )}>
          <div className="flex items-center gap-1">
            <Calendar className="h-3.5 w-3.5" />
            <span>Name</span>
          </div>
          <div>Due</div>
          {isWide && <div>Workflow Run</div>}
          <div>Workflow</div>
        </div>

        {/* Table Body */}
        <div className="flex-1 overflow-y-auto">
          {visibleItems.map((task) => (
            <div
              key={task.id}
              onClick={() => handleTaskClick(task)}
              className={cn(
                "grid gap-2 py-2 border-b border-gray-100 hover:bg-gray-50 cursor-pointer transition-colors items-center text-sm",
                isWide ? "grid-cols-[1fr_80px_120px_100px]" : "grid-cols-[1fr_80px_100px]"
              )}
            >
              {/* Name */}
              <div className="flex items-center gap-2 min-w-0">
                {task.isWorkflowTask ? (
                  <GitBranch className="h-4 w-4 text-blue-500 shrink-0" />
                ) : (
                  <Clock className="h-4 w-4 text-gray-400 shrink-0" />
                )}
                <span className="truncate font-medium">{task.name}</span>
              </div>

              {/* Due Date */}
              <div className="text-xs text-gray-600">
                {task.due_date ? formatDueDate(task.due_date) : '-'}
              </div>

              {/* Workflow Run (wide only) */}
              {isWide && (
                <div className="text-xs text-gray-600 truncate">
                  {task.isWorkflowTask ? task.workflow_run_name : '-'}
                </div>
              )}

              {/* Workflow/Space */}
              <div className="text-xs text-purple-600 truncate">
                {task.isWorkflowTask ? task.workflow_name : task.projects?.name}
              </div>
            </div>
          ))}
        </div>

        {/* Footer */}
        {remainingCount > 0 && (
          <button
            onClick={() => navigate('/tasks')}
            className="mt-2 pt-2 border-t text-center text-sm text-blue-600 hover:text-blue-700"
          >
            +{remainingCount} more
          </button>
        )}
      </div>
    );
  }

  // Default/Medium Layout
  return (
    <div ref={containerRef} className="flex flex-col h-full p-2">
      <div className="flex-1 space-y-2 overflow-hidden">
        {Object.entries(grouped).map(([date, tasks]) => (
          <div key={date}>
            <p className="text-[10px] font-medium text-gray-500 mb-1">
              {formatDueDate(date)}
            </p>
            <div className="space-y-1">
              {tasks.map((task) => (
                <div
                  key={task.id}
                  onClick={() => handleTaskClick(task)}
                  className="flex items-center gap-2 p-1.5 rounded-lg hover:bg-gray-50 cursor-pointer transition-colors"
                >
                  {task.isWorkflowTask ? (
                    <GitBranch className="h-3.5 w-3.5 text-blue-500 shrink-0" />
                  ) : (
                    <Clock className="h-3.5 w-3.5 text-gray-400 shrink-0" />
                  )}
                  <div className="flex-1 min-w-0">
                    <p className="text-xs font-medium truncate">{task.name}</p>
                    <p className="text-[10px] text-gray-500 truncate">
                      {task.isWorkflowTask ? task.workflow_name : task.projects?.name}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>

      {remainingCount > 0 && (
        <button
          onClick={() => navigate('/tasks')}
          className="text-xs text-blue-600 hover:text-blue-700 pt-1"
        >
          +{remainingCount} more
        </button>
      )}
    </div>
  );
}
